import { formatDollarsFromCents } from "lib";

export enum RoutingSide {
  Individual = "individual",
  Cash = "cash",
}

export function findSaleLotsForBuyer(
  saleLotsById: Record<string, SaleLot>,
  livestockSaleId: number,
  buyerId: string,
  cashBuyerBusinessId: string | null,
): SaleLot[] {
  return Object.values(saleLotsById).filter(saleLot => {
    if (saleLot.livestocksale_id !== livestockSaleId) return false;

    const resolvedBusinessId = saleLot.invoiceToBusinessId || saleLot.buyer_id;
    if (resolvedBusinessId === buyerId) return true;

    // Already routed to the cash buyer by a previous confirm - still this
    // buyer's lot for the purpose of toggling it back to individual.
    return (
      saleLot.buyer_id === buyerId && resolvedBusinessId === cashBuyerBusinessId
    );
  });
}

export interface BuyerSaleLotSummary {
  lotCount: number;
  totalPaidCents: number;
  totalLotValueCents: number;
}

/**
 * Mirrors the "Paid" figure on the Clearing Sale buyer card
 * (ClearingSaleCards/SaleLotSummary.js) - the buyer's actual payments, not
 * their lots' sale value, since that's what decides Cash Account routing.
 * totalLotValueCents is the lots' sale value itself, shown alongside the
 * payment figure so a row can be read without cross-referencing the lots.
 */
export function summarizeBuyerSaleLots(
  saleLots: SaleLot[],
  buyerId: string,
  paymentsById: Record<string, Payment>,
): BuyerSaleLotSummary {
  const totalPaidCents = Object.values(paymentsById)
    .filter(payment => payment.fromBusinessId === buyerId)
    .reduce((sum, payment) => sum + (payment.totalAmountCents || 0), 0);

  const totalLotValueCents = saleLots.reduce(
    (sum, saleLot) => sum + saleLot.total_price_cents,
    0,
  );

  return { lotCount: saleLots.length, totalPaidCents, totalLotValueCents };
}

export interface ConfirmPayload {
  buyerIdsToRouteToCash: string[];
  buyerIdsToRouteToIndividual: string[];
}

export function buildConfirmPayload(
  suggestions: AccountingDayRoutingSuggestion[],
  routingByBuyerId: Record<string, boolean>,
  saleLotsById: Record<string, SaleLot>,
  livestockSaleId: number,
  cashBuyerBusinessId: string | null,
): ConfirmPayload {
  const buyerIdsToRouteToCash: string[] = [];
  const buyerIdsToRouteToIndividual: string[] = [];

  suggestions.forEach(suggestion => {
    const isRoutingToCash = Boolean(routingByBuyerId[suggestion.id]);
    const targetInvoiceToBusinessId = isRoutingToCash
      ? cashBuyerBusinessId
      : null;

    const pendingSaleLots = findSaleLotsForBuyer(
      saleLotsById,
      livestockSaleId,
      suggestion.id,
      cashBuyerBusinessId,
    ).filter(
      saleLot =>
        (saleLot.invoiceToBusinessId || null) !== targetInvoiceToBusinessId,
    );

    if (pendingSaleLots.length === 0) return;

    if (isRoutingToCash) {
      buyerIdsToRouteToCash.push(suggestion.id);
    } else {
      buyerIdsToRouteToIndividual.push(suggestion.id);
    }
  });

  return { buyerIdsToRouteToCash, buyerIdsToRouteToIndividual };
}

/**
 * Where the backend's suggestion would put this buyer, absent any manual
 * override from the user.
 */
export function getSuggestedSide(
  suggestion: AccountingDayRoutingSuggestion,
): RoutingSide {
  if (suggestion.suggestCashAccount) {
    return RoutingSide.Cash;
  }
  return RoutingSide.Individual;
}

/**
 * Where a buyer currently sits: an explicit user override always wins; failing
 * that, a buyer already routed to Cash Account stays there - that's their
 * actual current state, not a suggestion to review. Failing that, the
 * algorithmic suggestion applies straight away.
 */
export function resolveBuyerDestination(
  suggestion: AccountingDayRoutingSuggestion,
  explicitDestinationByBuyerId: Record<string, RoutingSide>,
): RoutingSide {
  const explicitDestination = explicitDestinationByBuyerId[suggestion.id];
  if (explicitDestination) {
    return explicitDestination;
  }

  if (suggestion.alreadyRoutedToCash) {
    return RoutingSide.Cash;
  }

  return getSuggestedSide(suggestion);
}

export function filterAndSortSuggestions(
  suggestions: AccountingDayRoutingSuggestion[],
  side: RoutingSide,
  explicitDestinationByBuyerId: Record<string, RoutingSide>,
  searchText: string,
  showFlaggedOnly: boolean,
  buyerNameById: Record<string, string>,
  saleLotSummaryByBuyerId: Record<string, BuyerSaleLotSummary>,
): AccountingDayRoutingSuggestion[] {
  const normalizedSearchText = searchText.trim().toLowerCase();

  const matchingSuggestions = suggestions.filter(suggestion => {
    const destination = resolveBuyerDestination(
      suggestion,
      explicitDestinationByBuyerId,
    );
    if (destination !== side) {
      return false;
    }

    if (normalizedSearchText) {
      const buyerName = (buyerNameById[suggestion.id] || "").toLowerCase();
      if (!buyerName.includes(normalizedSearchText)) {
        return false;
      }
    }

    if (
      side === RoutingSide.Individual &&
      showFlaggedOnly &&
      !suggestion.suggestCashAccount
    ) {
      return false;
    }

    return true;
  });

  return matchingSuggestions.sort((a, b) => {
    if (
      side === RoutingSide.Individual &&
      a.suggestCashAccount !== b.suggestCashAccount
    ) {
      return a.suggestCashAccount ? -1 : 1;
    }

    const totalPaidCentsA = saleLotSummaryByBuyerId[a.id]?.totalPaidCents || 0;
    const totalPaidCentsB = saleLotSummaryByBuyerId[b.id]?.totalPaidCents || 0;
    return totalPaidCentsB - totalPaidCentsA;
  });
}

export enum IndividualColumnReason {
  ExistingXeroContact = "existing_xero_contact",
}

export const IndividualColumnReasonText: Record<
  IndividualColumnReason,
  string
> = {
  [IndividualColumnReason.ExistingXeroContact]: "Linked to a Xero Contact",
};

/**
 * Every reason a buyer sits in Individual Invoicing that isn't self-evident
 * from the paid amount already shown on the row: today that's just a buyer
 * who's an existing Xero Contact, which excludes them from
 * should_suggest_cash_account on the backend regardless of how much they've
 * paid.
 */
export function getIndividualColumnReasons(
  suggestion: AccountingDayRoutingSuggestion,
  isExistingXeroContact: boolean,
): IndividualColumnReason[] {
  if (suggestion.suggestCashAccount) {
    return [];
  }

  const reasons: IndividualColumnReason[] = [];

  if (isExistingXeroContact) {
    reasons.push(IndividualColumnReason.ExistingXeroContact);
  }

  return reasons;
}

export function buildColumnSummaryText(
  rows: AccountingDayRoutingSuggestion[],
  selectedBuyerIds: string[],
  saleLotSummaryByBuyerId: Record<string, BuyerSaleLotSummary>,
): string {
  const totalCents = rows.reduce(
    (sum, row) => sum + (saleLotSummaryByBuyerId[row.id]?.totalPaidCents || 0),
    0,
  );
  const selectedCents = selectedBuyerIds.reduce(
    (sum, buyerId) =>
      sum + (saleLotSummaryByBuyerId[buyerId]?.totalPaidCents || 0),
    0,
  );

  const buyerLabel = rows.length === 1 ? "buyer" : "buyers";
  const formattedTotal = formatDollarsFromCents(totalCents, { format: true });
  const formattedSelected = formatDollarsFromCents(selectedCents, {
    format: true,
  });

  return `Selected: ${selectedBuyerIds.length}, ${formattedSelected} / Total: ${rows.length} ${buyerLabel}, ${formattedTotal}`;
}
